/**
 * P38.4 -- Engine analyser surface inside the Service admin drawer.
 *
 * Renders inside the drawer's `extraSections` slot when editing a
 * Service. Shows:
 *   - the methodology status (which 5 fields are populated / NULL)
 *   - the "Suggest thresholds (Engine)" button (gated on >=1 NULL +
 *     Engine configured)
 *   - the most recent succeeded run's suggestions in a per-field
 *     review modal triggered by "Review suggestions"
 *   - polling at 2s while a run is queued/running; terminal-state
 *     toast (success/error) fires via the existing P35 toast
 *     primitive
 */
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "shared/api/client";
import { useToast } from "shared/toast/ToastProvider";
import { EngineLanguageNote } from "shared/components/EngineLanguageNote";

import type {
  Service,
  ServiceBizCrit,
  ServiceCyberCrit,
  ServicePatch,
} from "./serviceAdapter";

const METHODOLOGY_FIELDS = [
  "biz_crit",
  "cyber_crit",
  "rto_hours",
  "rpo_hours",
  "financial_impact_sar",
] as const;

type MethodologyField = (typeof METHODOLOGY_FIELDS)[number];

const FIELD_LABEL: Record<MethodologyField, string> = {
  biz_crit: "Business criticality",
  cyber_crit: "Cyber criticality",
  rto_hours: "RTO (hours)",
  rpo_hours: "RPO (hours)",
  financial_impact_sar: "Financial impact / hour (SAR)",
};

interface FieldSuggestion {
  field: MethodologyField;
  suggested_value: string | number;
  reasoning: string;
  confidence: "high" | "medium" | "low";
}

interface OmittedField {
  field: MethodologyField;
  reason: string;
}

interface RunOutputPayload {
  suggested_fields?: FieldSuggestion[];
  omitted_fields?: OmittedField[];
  reason?: string;
}

export interface TwinAnalyserRun {
  id: string;
  service_id: string;
  status: "queued" | "running" | "succeeded" | "failed";
  mode: "single_service" | "batch_gaps";
  auto_dispatched: boolean;
  model: string | null;
  prompt_version: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  started_at: string | null;
  finished_at: string | null;
  error_detail: string | null;
  output_payload: RunOutputPayload | null;
  created_at: string;
  updated_at: string;
}

interface Props {
  rowId: string;
}

export function ServiceAnalyserSection({ rowId }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();

  // The Service row -- methodology values surface as the BEFORE
  // column in the suggestion modal.
  const serviceQuery = useQuery<Service>({
    queryKey: ["foundation-admin", "services", rowId] as const,
    queryFn: () =>
      api.get<Service>(`/v1/admin/foundation/services/${encodeURIComponent(rowId)}`),
  });

  // Engine runs for this Service; poll at 2s while any open.
  const runsQuery = useQuery<TwinAnalyserRun[]>({
    queryKey: ["twin-admin", "service-analyser-runs", rowId] as const,
    queryFn: () =>
      api.get<TwinAnalyserRun[]>(
        `/v1/admin/twin/services/${encodeURIComponent(rowId)}/analyser-runs`,
      ),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const anyOpen = data.some((r) => r.status === "queued" || r.status === "running");
      return anyOpen ? 2_000 : false;
    },
  });

  const dispatch = useMutation<TwinAnalyserRun>({
    mutationFn: () =>
      api.post<TwinAnalyserRun>(
        `/v1/admin/twin/services/${encodeURIComponent(rowId)}/analyse-thresholds`,
        {},
      ),
    onSuccess: (queued) => {
      toast.push({
        tone: "info",
        message: "Engine analyser queued",
        detail: "We'll surface suggestions when the model returns.",
      });
      if (queued?.id) toastedRunIdsRef.current.add(queued.id);
      void qc.invalidateQueries({
        queryKey: ["twin-admin", "service-analyser-runs", rowId] as const,
      });
    },
    onError: () => {
      toast.push({
        tone: "error",
        message: "Engine dispatch failed",
        detail: "Engine may not be configured; check status.",
      });
    },
  });

  // Terminal-state toast tracker -- one toast per run id.
  const toastedRunIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const runs = runsQuery.data;
    if (!runs) return;
    for (const run of runs) {
      if (run.status !== "succeeded" && run.status !== "failed") continue;
      if (toastedRunIdsRef.current.has(run.id)) continue;
      toastedRunIdsRef.current.add(run.id);
      if (run.status === "succeeded") {
        const count = run.output_payload?.suggested_fields?.length ?? 0;
        toast.push({
          tone: "success",
          message:
            count > 0
              ? `Engine returned ${count} threshold suggestion${count === 1 ? "" : "s"}`
              : "Engine ran -- no gaps to fill",
          ...(count > 0
            ? {
                detail: "Open the latest run to review and accept per field.",
              }
            : {}),
        });
      } else {
        toast.push({
          tone: "error",
          message: "Engine run failed",
          ...(run.error_detail ? { detail: run.error_detail } : {}),
        });
      }
    }
  }, [runsQuery.data, toast]);

  const latestSucceeded = useMemo(() => {
    return (runsQuery.data ?? []).find(
      (r) =>
        r.status === "succeeded" &&
        (r.output_payload?.suggested_fields?.length ?? 0) > 0,
    );
  }, [runsQuery.data]);

  const [showReview, setShowReview] = useState(false);

  const service = serviceQuery.data;
  const nullFieldCount = useMemo(() => {
    if (!service) return 0;
    return METHODOLOGY_FIELDS.filter((f) => service[f] === null).length;
  }, [service]);

  const hasOpenRun = (runsQuery.data ?? []).some(
    (r) => r.status === "queued" || r.status === "running",
  );

  if (!service) return null;

  const buttonDisabled = nullFieldCount === 0 || dispatch.isPending || hasOpenRun;

  return (
    <section data-testid="service-analyser-section" className="space-y-2">
      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
        {t("screens.serviceAnalyserSection.marsadEngineMethodologySuggestions")}
      </h3>

      <MethodologyStatusGrid service={service} />

      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="service-analyser-dispatch"
          data-queued={hasOpenRun || dispatch.isPending}
          onClick={() => void dispatch.mutateAsync()}
          disabled={buttonDisabled}
          className={
            "rounded border px-3 py-1 text-xs font-semibold disabled:opacity-50 " +
            (buttonDisabled
              ? "border-slate-300 bg-slate-100 text-slate-500"
              : "border-brand-brassDark bg-brand-brass text-brand-navy")
          }
        >
          {dispatch.isPending
            ? t("screens.serviceAnalyserSection.dispatching")
            : hasOpenRun
              ? t("screens.serviceAnalyserSection.queued")
              : nullFieldCount === 0
                ? t("screens.serviceAnalyserSection.nothingToSuggest")
                : `Suggest thresholds (Engine, ${nullFieldCount} NULL)`}
        </button>

        {latestSucceeded && (
          <button
            type="button"
            data-testid="service-analyser-review"
            onClick={() => setShowReview(true)}
            className="rounded border border-emerald-400 bg-white px-3 py-1 text-xs font-semibold text-emerald-800"
          >
            {t("screens.serviceAnalyserSection.reviewLastSuggestions")}
          </button>
        )}
      </div>

      {showReview && latestSucceeded && (
        <SuggestionReviewModal
          service={service}
          run={latestSucceeded}
          onClose={() => setShowReview(false)}
          onAccepted={() => {
            setShowReview(false);
            void qc.invalidateQueries({
              queryKey: ["foundation-admin", "services"] as const,
            });
          }}
        />
      )}
    </section>
  );
}

// ── Methodology status grid ─────────────────────────────────────────

function MethodologyStatusGrid({ service }: { service: Service }) {
  const { t } = useTranslation();
  return (
    <ul
      data-testid="service-methodology-status"
      className="grid grid-cols-1 gap-1 text-xs sm:grid-cols-2"
    >
      {METHODOLOGY_FIELDS.map((f) => {
        const v = service[f];
        const isNull = v === null;
        return (
          <li
            key={f}
            data-testid={`methodology-field-${f}`}
            data-is-null={isNull}
            className="flex items-center justify-between gap-2 rounded border border-slate-200 bg-white px-2 py-1"
          >
            <span className="text-slate-600">{FIELD_LABEL[f]}</span>
            {isNull ? (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-900">
                {t("screens.serviceAnalyserSection.null")}
              </span>
            ) : (
              <span className="font-mono text-slate-900">{String(v)}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ── Suggestion review modal ─────────────────────────────────────────

interface SuggestionReviewModalProps {
  service: Service;
  run: TwinAnalyserRun;
  onClose: () => void;
  onAccepted: () => void;
}

function SuggestionReviewModal({
  service,
  run,
  onClose,
  onAccepted,
}: SuggestionReviewModalProps) {
  const { t } = useTranslation();
  const suggestions = run.output_payload?.suggested_fields ?? [];
  const omitted = run.output_payload?.omitted_fields ?? [];

  const [selected, setSelected] = useState<Set<MethodologyField>>(
    () => new Set(suggestions.map((s) => s.field)),
  );

  const toggle = (field: MethodologyField) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const patch: ServicePatch = {};
      for (const s of suggestions) {
        if (!selected.has(s.field)) continue;
        if (s.field === "biz_crit") {
          patch.biz_crit = s.suggested_value as ServiceBizCrit;
        } else if (s.field === "cyber_crit") {
          patch.cyber_crit = s.suggested_value as ServiceCyberCrit;
        } else if (s.field === "rto_hours" || s.field === "rpo_hours") {
          patch[s.field] = Number(s.suggested_value);
        } else if (s.field === "financial_impact_sar") {
          patch.financial_impact_sar = String(s.suggested_value);
        }
      }
      return api.patch<Service>(
        `/v1/admin/foundation/services/${encodeURIComponent(service.id)}`,
        patch,
      );
    },
    onSuccess: () => {
      onAccepted();
    },
  });

  return (
    <div
      data-testid="suggestion-review-modal"
      role="dialog"
      aria-label="Engine suggestion review"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
    >
      <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded border border-slate-200 bg-white shadow-xl">
        <header className="border-b border-slate-200 p-3">
          <h2 className="text-sm font-semibold text-slate-900">
            {t("screens.serviceAnalyserSection.engineSuggestionsFor")} {service.name}
          </h2>
          <p className="text-[11px] text-slate-500">
            {t("screens.serviceAnalyserSection.model")}{" "}
            {run.model ?? t("screens.serviceAnalyserSection.unknown")}{" "}
            {t("screens.serviceAnalyserSection.prompt")} {run.prompt_version} ·
            {run.tokens_in ?? 0} {t("screens.serviceAnalyserSection.in")}{" "}
            {run.tokens_out ?? 0} {t("screens.serviceAnalyserSection.outTokens")}
          </p>
        </header>

        <ul className="divide-y divide-slate-200">
          {suggestions.map((s) => (
            <li
              key={s.field}
              data-testid={`suggestion-row-${s.field}`}
              className="flex items-start gap-3 p-3"
            >
              <input
                type="checkbox"
                data-testid={`suggestion-accept-${s.field}`}
                checked={selected.has(s.field)}
                onChange={() => toggle(s.field)}
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-semibold text-slate-900">
                    {FIELD_LABEL[s.field]}
                  </span>
                  <ConfidenceBadge confidence={s.confidence} />
                </div>
                <div className="mt-1 grid grid-cols-2 gap-2 text-[11px]">
                  <div>
                    <span className="block text-slate-500">
                      {t("screens.serviceAnalyserSection.current")}
                    </span>
                    <span className="font-mono text-slate-700">
                      {service[s.field] === null
                        ? t("screens.serviceAnalyserSection.null")
                        : String(service[s.field])}
                    </span>
                  </div>
                  <div>
                    <span className="block text-slate-500">
                      {t("screens.serviceAnalyserSection.suggested")}
                    </span>
                    <span className="font-mono text-emerald-800">
                      {String(s.suggested_value)}
                    </span>
                  </div>
                </div>
                <p className="mt-1 text-[11px] italic text-slate-600">{s.reasoning}</p>
              </div>
            </li>
          ))}
        </ul>
        <div className="px-3">
          <EngineLanguageNote />
        </div>

        {omitted.length > 0 && (
          <div className="border-t border-slate-200 bg-amber-50 p-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-amber-900">
              {t("screens.serviceAnalyserSection.omittedTrackedAsOpenGaps")}
            </h3>
            <ul className="mt-1 space-y-1">
              {omitted.map((o) => (
                <li
                  key={o.field}
                  data-testid={`omitted-row-${o.field}`}
                  className="text-[11px] text-amber-900"
                >
                  <span className="font-semibold">{FIELD_LABEL[o.field]}:</span>{" "}
                  {o.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        <footer className="flex items-center justify-end gap-2 border-t border-slate-200 p-3">
          <button
            type="button"
            data-testid="suggestion-modal-cancel"
            onClick={onClose}
            className="rounded border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700"
          >
            {t("screens.serviceAnalyserSection.cancel")}
          </button>
          <button
            type="button"
            data-testid="suggestion-modal-accept"
            disabled={selected.size === 0 || acceptMutation.isPending}
            onClick={() => void acceptMutation.mutateAsync()}
            className="rounded border border-brand-brassDark bg-brand-brass px-3 py-1 text-xs font-semibold text-brand-navy disabled:opacity-50"
          >
            {acceptMutation.isPending
              ? t("screens.serviceAnalyserSection.applying")
              : `Accept ${selected.size} field${selected.size === 1 ? "" : "s"}`}
          </button>
        </footer>
      </div>
    </div>
  );
}

function ConfidenceBadge({ confidence }: { confidence: string }) {
  const tone =
    confidence === "high"
      ? "bg-emerald-100 text-emerald-900"
      : confidence === "medium"
        ? "bg-slate-100 text-slate-800"
        : "bg-amber-100 text-amber-900";
  return (
    <span
      data-testid="confidence-badge"
      className={`rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${tone}`}
    >
      {confidence}
    </span>
  );
}
